import React, { Ref, PropsWithChildren, useEffect, useRef, useState } from 'react';
import {
  TbAlignCenter,
  TbAlignLeft,
  TbAlignRight,
  TbArrowBarBoth,
  TbArrowBarLeft,
  TbArrowBarRight,
  TbArrowLeftSquare,
  TbArticle,
  TbArticleFilledFilled,
  TbBaseline,
  TbBaselineDensityMedium,
  TbBold,
  TbH1,
  TbH2,
  TbItalic,
  TbLink,
  TbList,
  TbListDetails,
  TbListNumbers,
  TbMoodSmile,
  TbQuote,
  TbTrash,
  TbUnderline,
} from 'react-icons/tb';
import { useSlate, useFocused } from 'slate-react';
import { BaseProps, BlockButton, FormatButton, GenericButton, MarkButton, Menu, OrNull, Portal } from './components';
import { EmojiPicker } from '../emojis';
import { AddLinkButton, LinkModal } from './links';
import { PopUp } from './popup';
import { TextColor } from 'types/domain/text';
import { ArrowsPointingOutIcon } from '@heroicons/react/24/outline';
import { ActionEntityPosition, ActionStyle } from 'types/domain/action';

export interface ToolbarProps extends PropsWithChildren<BaseProps> {
  onDelete?: (e) => void;
  setEmojiOpen: (val) => void;
  setLinkOpen: (val) => void;
  setColor: (val) => void;
  onExpand?: (val) => void;
  onPosition?: (val) => void;
  onStyle?: (val) => void;
  color: number;
  style?: ActionStyle | number;
  emojiOpen: boolean;
  linkOpen: boolean;
  parentRef?: any;

  position?: ActionEntityPosition;
}

export const Toolbar = React.forwardRef(
  (
    { className, setEmojiOpen, setLinkOpen, emojiOpen, linkOpen, ...props }: ToolbarProps,
    ref: Ref<OrNull<HTMLDivElement>>,
  ) => {
    const iconClass = 'w-4 h-4 text-gray-800';

    return (
      <>
        <Menu
          ref={ref}
          className="mx-auto py-1 px-2 mt-6 rounded-md overflow-visible shadow-lg text-grey-800 bg-white dark:bg-slate-900 dark:text-white border border-slate-700/40"
        >
          <FormatButton format="bold" icon={<TbBold className={iconClass} />} />
          <FormatButton format="italic" icon={<TbItalic className={iconClass} />} />
          <FormatButton format="underline" icon={<TbUnderline className={iconClass} />} />
          <PopUp icon={<TbH1 className={iconClass} />}>
            <BlockButton format="heading-one" icon={<TbH1 className={iconClass} />} />
            <BlockButton format="heading-two" icon={<TbH2 className={iconClass} />} />
            <BlockButton format="block-quote" icon={<TbQuote className={iconClass} />} />
          </PopUp>
          <PopUp icon={<TbList className={iconClass} />}>
            <BlockButton format="bulleted-list" icon={<TbList className={iconClass} />} />
            <BlockButton format="numbered-list" icon={<TbListNumbers className={iconClass} />} />
          </PopUp>

          <PopUp icon={<TbAlignLeft className={iconClass} />}>
            <BlockButton format="left" icon={<TbAlignLeft className={iconClass} />} />
            <BlockButton format="center" icon={<TbAlignCenter className={iconClass} />} />
            <BlockButton format="right" icon={<TbAlignRight className={iconClass} />} />
          </PopUp>
          {props.onPosition && (
            <PopUp
              icon={
                props.position ? (
                  props.position === ActionEntityPosition.LEFT ? (
                    <TbArrowBarLeft className={iconClass} />
                  ) : props.position === ActionEntityPosition.RIGHT ? (
                    <TbArrowBarRight className={iconClass} />
                  ) : (
                    <TbArrowBarBoth className={iconClass} />
                  )
                ) : (
                  <TbArrowBarBoth className={iconClass} />
                )
              }
            >
              <GenericButton
                onClick={e => props.onPosition(ActionEntityPosition.LEFT)}
                icon={<TbArrowBarLeft className={iconClass} />}
              />
              <GenericButton
                onClick={e => props.onPosition(ActionEntityPosition.CENTER)}
                icon={<TbArrowBarBoth className={iconClass} />}
              />
              <GenericButton
                onClick={e => props.onPosition(ActionEntityPosition.RIGHT)}
                icon={<TbArrowBarRight className={iconClass} />}
              />
            </PopUp>
          )}
          {props.setColor && <Color color={props.color} setColor={props.setColor} />}
          {props.onStyle && (
            <GenericButton
              onClick={e => props.onStyle(props.style === ActionStyle.BOX ? ActionStyle.LINES : ActionStyle.BOX)}
              icon={
                props.style !== ActionStyle.BOX ? (
                  <TbArticleFilledFilled className={iconClass} />
                ) : (
                  <TbArticle className={iconClass} />
                )
              }
            />
          )}

          <GenericButton
            onClick={e => {
              e.stopPropagation();
              setEmojiOpen(true);
            }}
            icon={<TbMoodSmile className={iconClass} />}
          />

          <div className="flex ml-auto mr-0">
            {/* <AddLinkButton onClick={() => setLinkOpen(true)}/> */}
            {props.onExpand && (
              <GenericButton onClick={props.onExpand} icon={<ArrowsPointingOutIcon className={iconClass} />} />
            )}
            {props.onDelete && <GenericButton onClick={props.onDelete} icon={<TbTrash className={iconClass} />} />}
          </div>
        </Menu>
        <div className="relative">
          {/* <EmojiPicker open={emojiOpen} setOpen={val => setEmojiOpen(val)} /> */}
          <LinkModal open={linkOpen} onClose={() => setLinkOpen(false)} />
        </div>
      </>
    );
  },
);

export const HoveringToolbar = ({
  parentRef,
  emojiOpen,
  setEmojiOpen,
  setColor,
  onDelete,
  color,
  inScene,
}: ToolbarProps) => {
  const ref = useRef<HTMLDivElement | null>();
  const editor = useSlate();
  const { selection } = editor;
  const inFocus = useFocused();

  useEffect(() => {
    const el = ref.current;
    const { selection } = editor;

    if (!el) {
      return;
    }

    if (!selection || !inFocus) {
      el.removeAttribute('style');
      return;
    }

    const rect = document.getElementById(parentRef).getBoundingClientRect();

    el.style.opacity = '1';
    el.style.zIndex = '100000000';
    el.style.top = `${rect.top + window.pageYOffset - el.offsetHeight}px`;
    el.style.left = `${rect.left + window.pageXOffset - el.offsetWidth / 2 + rect.width / 2}px`;
  });

  const iconClass = 'w-6 h-6 p-1';

  return (
    <>
      <Portal>
        <Menu
          inScene
          ref={ref}
          className="absolute top-1 opacity-0 py-1 px-2  rounded-md shadow-lg text-gray-800 bg-white dark:bg-slate-900 dark:text-white border  border-slate-700"
          onMouseDown={e => {
            // prevent toolbar from taking focus away from editor
            e.preventDefault();
          }}
        >
          <FormatButton format="bold" icon={<TbBold className={iconClass} />} />
          <FormatButton format="italic" icon={<TbItalic className={iconClass} />} />
          <FormatButton format="underline" icon={<TbUnderline className={iconClass} />} />
          <PopUp icon={<TbH1 className={iconClass} />}>
            <BlockButton format="heading-one" icon={<TbH1 className={iconClass} />} />
            <BlockButton format="heading-two" icon={<TbH2 className={iconClass} />} />
          </PopUp>
          <PopUp icon={<TbAlignLeft className={iconClass} />}>
            <BlockButton format="left" icon={<TbAlignLeft className={iconClass} />} />
            <BlockButton format="center" icon={<TbAlignCenter className={iconClass} />} />
            <BlockButton format="right" icon={<TbAlignRight className={iconClass} />} />
          </PopUp>
          {/* <PopUp icon={<TbList className={iconClass} />}>
            <BlockButton format="bulleted-list" icon={<TbList className={iconClass} />} />
            <BlockButton format="numbered-list" icon={<TbListNumbers className={iconClass} />} />
          </PopUp> */}
          {setColor && <Color color={color} setColor={setColor} />}

          <GenericButton
            onClick={e => {
              e.preventDefault();
              setEmojiOpen(true);
            }}
            icon={<TbMoodSmile className={iconClass} />}
          />
          <GenericButton onClick={onDelete} icon={<TbTrash className={iconClass} />} />
        </Menu>
        <div className="relative">{/* <EmojiPicker open={emojiOpen} setOpen={val => setEmojiOpen(val)} /> */}</div>
      </Portal>
    </>
  );
};

export function Color({ color, setColor }) {
  const className = 'w-5 h-5 rounded-sm';
  return (
    <>
      <PopUp
        icon={
          <div
            className={`${className} ${
              color === TextColor.BRAND
                ? 'bg-brand'
                : color === TextColor.WHITE
                ? 'bg-white border border-black/20'
                : 'bg-black'
            } `}
          />
        }
      >
        <button
          onClick={e => {
            //e.preventDefault();
            setColor(TextColor.BRAND);
          }}
        >
          <div className={`${className}  bg-brand`} />
        </button>
        <button
          onClick={e => {
            // e.preventDefault();
            setColor(TextColor.BLACK);
          }}
        >
          <div className={`${className}  bg-black`} />
        </button>
        <button
          onClick={e => {
            //e.preventDefault();
            setColor(TextColor.WHITE);
          }}
        >
          <div className={`${className}   bg-white border border-black/20`} />
        </button>
      </PopUp>
    </>
  );
}
